<template>
  <DropDown :trigger="'contextmenu'" :menus="menu">
    <div
      ref="workBenchRef"
      class="relative w-full overflow-hidden _workBenchRef"
      @mousedown="onDrapMouseDown"
      @wheel="onScaleWheel"
      @contextmenu="onContextmenu"
    >
      <WatchFaceSku
        class="absolute"
        :style="{
          transform: `scale(${editorStore.watchfaceCanvas.scale})`
        }"
      />

      <!-- watchface renderer -->
      <div
        class="absolute box-content _center"
        :style="{
          borderRadius: watchfaceRaduis,
          left: editorStore.watchfaceCanvas.offsetX + 'px',
          top: editorStore.watchfaceCanvas.offsetY + 'px',
          // 解决表盘位置跳动问题
          opacity: watchfaceStore.size.width > 0 ? 1 : 0,
          backgroundColor: editorStore.theme?.bgColor
            ? editorStore.theme?.bgColor
            : '#000000'
        }"
      >
        <canvas
          id="watch-canvas"
          ref="canvasRef"
          :width="watchfaceStore.size.width"
          :height="watchfaceStore.size.height"
          :style="{
            borderRadius: 'inherit',
            boxSizing: 'content-box'
          }"
        />
      </div>
      <KonvaShapes :width="konvaSize.width" :height="konvaSize.height" />
    </div>
  </DropDown>
  <modal-shape-export ref="exportRef" />
  <modal-template-create ref="templateRef" />
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useCanvasRender } from '@/hooks/use-canvas-render';
import { EventType } from '@/utils/emitter.utils';
import { useEditorStore } from '@/store/use-editor-store';
import {
  useWorkbench,
  watchfaceOpening,
  useEmitterListener,
  useWatchfaceScreenShot,
  getWatchfaceDeviceRadius,
  watchfaceImageLoading
} from '@/hooks';
import KonvaService from '@/services/konva';
import { Attributes } from '@/services/shape/attributes';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { debounce } from 'lodash-es';
import { PopupType, useEditorPopups } from '@/hooks/use-editor-popups';
import { Shape } from '@/services/shape';
import { useLayerMenu } from '@/hooks/use-layer-menu';
import { saveAs } from 'file-saver';
import { dataURItoBlob } from '@/utils/fs/image.utils';
import { useIFTTTStore } from '@/store/use-ifttt-store';
const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();

const {
  workBenchRef,
  canvasRef,
  konvaSize,
  onDrapMouseDown,
  watchfaceRaduis,
  onScaleWheel,
  spacePressed,
  scaleDown,
  scaleTo100,
  scaleUp,
  centerCanvas
} = useWorkbench();

const { render } = useCanvasRender(canvasRef);
const { screenshot } = useWatchfaceScreenShot();

const _renderDataChange = debounce(() => {
  if (watchfaceOpening.value || watchfaceImageLoading.value) return;
  render();
}, 50);

const _renderKeepData = debounce(() => render({ reason: 'keep-data' }), 50);
const _renderWindowResize = debounce(
  () => render({ reason: 'window-resize' }),
  50
);

useEmitterListener(
  [
    EventType['shape:deleted'],
    EventType['shape:sloted'],
    EventType['shape:unsloted'],
    EventType['shape:widgeted'],
    EventType['shape:unwidgeted'],
    EventType['shape:copy'],

    EventType['theme:create'],
    EventType['theme:selected'],
    EventType['theme:set-attr'],
    EventType['theme-color:create'],
    EventType['theme-color:selected'],
    EventType['theme-color:set-attr'],

    EventType['snapshot:restore'],
    EventType['watchface:opened'],
    EventType['watchface:description-update']
  ],
  () => {
    _renderDataChange();
  }
);
useEmitterListener(
  EventType['shape:drag'],
  (data?: { parentId?: string; shapeId?: string; preParentId?: string; isSameParentReorder?: boolean }) => {
    if (data?.isSameParentReorder) {
      // 同父级排序（纯 z-order 变更）：需要重新渲染画布以反映新的图层顺序
      _renderDataChange();
      // 同时刷新konva覆盖层
      const service = KonvaService.getIns();
      if (service?.enable) {
        const attrDisabled = editorStore.attrDisabled;
        const _shapes = Shape.shapeTree(editorStore.themeId, false);
        service.drawShapes(_shapes, {
          selected: editorStore.selectedIds,
          disableDrag: !!attrDisabled,
          disableTransform: !!attrDisabled
        });
      }
    } else {
      // 跨父级移动：需要完整重新渲染
      _renderDataChange();
    }
  }
);
useEmitterListener(
  [EventType['shape:set-attr']],
  (data: { shapeId?: string; attrs?: (keyof Attributes)[] } = {}) => {
    const shape = Shape.getShape(data.shapeId ?? '');
    if (!shape) return;

    const { x, y, rotation, w, h } = shape.attrs;
    const immediateKeys = ['x', 'y', 'w', 'h'];
    const keys = [...(data?.attrs ?? [])].filter((key) => {
      return !['sourceEnabled', 'showImgFormat'].includes(key);
    });
    if (!keys.length) return;

    if (keys?.every((key) => immediateKeys.includes(key))) {
      const data: Parameters<typeof render>[0] = {
        reason: 'shape-transformed',
        layouts: [
          {
            layoutId: shape.id,
            data: shape.widgetImageArray
              ? { x, y, rotation }
              : { x, y, rotation, w, h }
          }
        ]
      };
      render(data);
    } else if (data.attrs?.length == 1 && data.attrs[0] == 'resources') {
      render({ reason: 'images-changed' });
    } else {
      _renderDataChange();
    }
  }
);
useEmitterListener(
  [EventType['konva:transformed']],
  (params?: { shapeIds: string[]; dragend?: boolean }) => {
    const { shapeIds = [], dragend = false } = params ?? {};
    const data: Parameters<typeof render>[0] = {
      reason: 'shape-transformed',
      layouts: []
    };
    shapeIds.forEach((shapeId) => {
      const shape = Shape.getShape(shapeId);
      if (!shape) return;
      const { x, y, rotation, w, h } = shape.attrs;
      // widgetImageArray 的 attrs.w/h 是单个数字图片尺寸，不是 Widget 的实际尺寸
      // 传入 w/h 会覆盖 Widget 的正确尺寸，导致 alignWidgetIfNeed 计算错误（子元素位移）
      const isWidgetImageArray = !!shape.widgetImageArray;
      data.layouts?.push({
        layoutId: shapeId,
        data: isWidgetImageArray ? { x, y, rotation } : { x, y, w, h, rotation }
      });
    });
    render(data);

    // 删除此功能，导出时进行验证即可
    // 拖拽时有可能将组内子元素的x y为负值
    // 在这里需要恢复，并提示用户
    // if (dragend) {
    //   shapeIds.forEach((shapeId) => {
    //     const shape = Shape.getShape(shapeId);
    //     if (!shape) return;
    //     const { x = 0, y = 0 } = shape.attrs;
    //     const data: Partial<Attributes> = {};
    //     if (x < shape.minXY) {
    //       data.x = 0;
    //     }
    //     if (y < shape.minXY) {
    //       data.y = 0;
    //     }
    //     if (Object.keys(data).length) {
    //       shape.setAttrs(data);
    //       showToast('组内子元素x、y坐标不可为负值');
    //     }
    //   });
    // }
  }
);
useEmitterListener([EventType['config:datachanged']], () => {
  render({
    reason: 'config-data-changed'
  });
});

useEmitterListener([EventType['shape:selected']], () => {
  if (editorStore.iftttPlaying) {
    editorStore.iftttPlaying = false;
    render({
      reason: 'others'
    });
  }
});

// 新创建shape后，需要触发更新图片逻辑，因为创建shape时，有可能
useEmitterListener(
  [EventType['shape:create'], EventType['shape:update-condition']],
  () => {
    render({
      reason: 'others'
    });
  }
);

watch(
  () => watchfaceStore.description.watchOS,
  () => {
    render({
      reason: 'others'
    });
  }
);

useEmitterListener(EventType['window:resize'], () => {
  if (watchfaceOpening.value || watchfaceImageLoading.value) return;
  _renderWindowResize();
});
useEmitterListener(EventType['watchface:font-loaded'], () => {
  if (watchfaceOpening.value || watchfaceImageLoading.value) return;
  _renderKeepData();
});

watch(
  () => editorStore.watchfaceCanvas.scale,
  () => {
    if (watchfaceOpening.value) return;
    if (watchfaceStore.size.width <= 0) return;
    render({
      reason: 'canvas-scale'
    });
  },
  { deep: true }
);
watch(
  () => editorStore.arcAuxiliary,
  () => {
    _renderDataChange();
  },
  {
    deep: true
  }
);
// 监听设备尺寸变化，重新渲染
watch(
  [() => watchfaceStore.size.width, () => watchfaceStore.size.height],
  () => {
    _renderDataChange();
  }
);

watch(spacePressed, (val) => {
  const service = KonvaService.getIns();
  if (!service) return;
  service.enable = val ? false : true;
  if (service.enable) render();
});

const menu = ref<DropDownMenuItem[]>([]);

const canvasMenu = [
  {
    title: '视窗背景颜色',
    onClick: () => useEditorPopups().togglePopup(PopupType['bgColorPicker'])
  },
  { delimiter: true },

  {
    title: '表盘居中',
    onClick: centerCanvas
  },
  { delimiter: true },
  {
    title: '表盘截屏',
    onClick: async () => {
      const themeName = editorStore.theme?.name;
      const themeType = editorStore.theme?.type;
      const themeColor = editorStore.themeColor?.color;
      const dataURI = await screenshot(undefined, {
        clipRound: getWatchfaceDeviceRadius(watchfaceStore.deviceType ?? ''),
        usingCurConfigData: true,
        themeName: themeName,
        themeType: themeType,
        themeColor
      });
      if (!dataURI) return;
      const blob = dataURItoBlob(dataURI as string);
      const name = `表盘截图_${themeName}_${editorStore.themeColor?.name}.png`;
      saveAs(blob, name);
    }
  },
  { delimiter: true },
  {
    title: '放大',
    shortcuts: ['+'],
    onClick: scaleUp
  },
  {
    title: '缩小',
    shortcuts: ['-'],
    onClick: scaleDown
  },
  {
    title: '缩放至100%',
    shortcuts: ['CommandOrControl', '0'],
    onClick: scaleTo100
  }
] as DropDownMenuItem[];

const templateRef = ref();
const exportRef = ref();

const { contextMenus } = useLayerMenu({
  isOnCanvas: true,
  handleTemplateModal: (shapeIds: string[]) =>
    templateRef.value.showModal(shapeIds),
  handleExportModal: (shapeId: string) => exportRef.value?.open?.(shapeId),
  handleRename: () => ({})
});

const onContextmenu = async () => {
  await nextTick();
  if (!editorStore.selectedIds?.length) {
    menu.value = canvasMenu;
  } else {
    menu.value = contextMenus.value;
  }
};

// 监听ifttt的变化
const iftttStore = useIFTTTStore();
iftttStore.$onAction(() => {
  _renderDataChange();
});
</script>

<style scoped lang="less">
._workBenchRef {
  min-width: calc(100vw * 1.2);
  min-height: calc(100vh * 1.5);
}
</style>
